import { abortUpload } from '@api/uploadFileWithProgression';
import { DocumentProcessingFlow } from '@common/features/document/components/DocumentContentDetails/types';
import { getCanUpload, getIsFileValid, IDocumentAttributes } from '@common/features/document/helpers';
import { useAddSecondaryDocumentOnModal } from '@common/features/document/hooks/useAddSecondaryFile';
import { AddModalOpenModes, FileRoles, FormFields } from '@common/features/document/types';
import { IDocumentDto } from '@common/features/submission/types';
import { removeHttpPrefixFromLink } from '@common/helpers';
import { useHandleInitialConversionResultForDocument } from '@common/hooks/useFetchDocumentConversionResults';
import { useFetchDocumentHtmlContent } from '@common/hooks/useFetchDocumentHtmlContent';
import { DocumentType, SubmissionTaskType, TaskTypeToDocumentTypeMap } from '@common/types';
import { useDeleteFile } from '@hooks/useDeleteFile';
import { useUploadFile } from '@hooks/useUploadFile';
import { useReplaceDocument } from 'apps/publisher-v3.ui.customer/src/pages/TaskDetails/hooks/useReplaceDocument';
import useAppNavigation from 'apps/publisher-v3.ui.customer/src/routing/useAppNavigation';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface IProps {
  methods: UseFormReturn<any>;
  taskId: string;
  taskType?: SubmissionTaskType;
  submissionId: string;
  existingDocuments: IDocumentDto[] | undefined;
  customerLink: string | undefined;
  documentAttributes: IDocumentAttributes;
  documentProcessingFlow: DocumentProcessingFlow | undefined;
  hasDigitalVersion: boolean | undefined;
  isCustomerApprovalRequired: boolean | undefined;
}

export const useReplaceDocumentModal = ({
  methods: { setValue, reset, getValues, handleSubmit },
  submissionId,
  taskId = '',
  taskType,
  existingDocuments,
  customerLink,
  documentAttributes,
  documentProcessingFlow,
  hasDigitalVersion,
  isCustomerApprovalRequired,
}: IProps) => {
  const secondaryPdfExpected = documentProcessingFlow === DocumentProcessingFlow.qrdPil ? false : true; // In replace QRD flow it's not expected
  const actualContentType = TaskTypeToDocumentTypeMap[taskType!]!;
  const detailsPageType = actualContentType === DocumentType.Epil ? DocumentType.Pil : actualContentType;

  const handleRemove = () => {
    setValue(FormFields.file, undefined);
    abortUpload();
    if (!isLoading) {
      deleteFile(fileName);
    }
  };

  const { uploadFile, isLoading, uploadProgress, fileName, fileErrors, setFileErrors } = useUploadFile(handleRemove);

  const secondaryDocument = useAddSecondaryDocumentOnModal({ contentType: actualContentType, setValue });
  const { addInitialConversionResultForDocument } = useHandleInitialConversionResultForDocument();

  const { fetchDocumentHtmlContent } = useFetchDocumentHtmlContent();

  const { goToReplacementUpdateTaskDocumentContentDetails } = useAppNavigation();
  const { deleteFile } = useDeleteFile();

  const { replaceDocument } = useReplaceDocument(
    parseInt(taskId as string),
    fileName,
    getValues(FormFields.file)?.name,
    getValues(FormFields.webLink),
    getValues(FormFields.content),
    getValues(FormFields.includeSecondaryDocument)!,
    getValues(FormFields.secondaryfile)?.name,
    secondaryDocument.fileName,
    documentAttributes,
  );

  const [isModalOpen, setIsModalOpen] = useState<AddModalOpenModes>(null);

  const handleFileSubmit = (files: FileList | null) => {
    const fileRole =
      documentProcessingFlow === DocumentProcessingFlow.qrdPil && actualContentType === DocumentType.Epil
        ? FileRoles.PrimaryDocFile
        : undefined;
    const file = files?.item(0);
    if (file) {
      const errors = getIsFileValid(actualContentType, file, fileRole);
      if (errors.length > 0) setFileErrors(errors);
      else {
        setValue(FormFields.file, file);
        uploadFile({ file, documentType: actualContentType, fileRole });
      }
    }
  };

  const handleUploadClick = () => {
    const errors = getCanUpload(
      actualContentType,
      getValues(FormFields.documentTitle),
      documentAttributes,
      getValues(FormFields.noAudioVideoUrl),
      getValues(FormFields.file),
      getValues(FormFields.webLink),
      getValues(FormFields.hasDigitalVersion),
      getValues(FormFields.isCustomerApprovalRequired),
      getValues(FormFields.content),
      secondaryPdfExpected && getValues(FormFields.includeSecondaryDocument),
      getValues(FormFields.secondaryfile),
      documentProcessingFlow,
    );

    if (errors.length > 0) setFileErrors(errors);
    else {
      handleSubmit(() =>
        replaceDocument(
          {
            title: getValues(FormFields.documentTitle),
            includeSecondaryDocument: secondaryPdfExpected && getValues(FormFields.includeSecondaryDocument),
          },
          {
            onSuccess: (data) => {
              goToReplacementUpdateTaskDocumentContentDetails(submissionId!, detailsPageType, data.documentId, taskId);
              addInitialConversionResultForDocument(data.documentId, detailsPageType, documentProcessingFlow!);
            },
          },
        ),
      )();
    }
  };

  const handleCancel = () => {
    setIsModalOpen(null);
    reset({});
    abortUpload();
  };

  const clearErrors = () => {
    setFileErrors([]);
    secondaryDocument.setFileErrors([]);
  };

  const handleOpenModal = async (productGroupName: string, documentId?: number, mode?: AddModalOpenModes) => {
    const selectedDocument = existingDocuments?.find((d) => d.documentId === Number(documentId));
    const documentTitle = selectedDocument?.documentTitle ?? productGroupName;
    let documentHtmlContent = '';
    if (documentAttributes.hasHtmlContent) {
      documentHtmlContent = await fetchDocumentHtmlContent({
        documentId: Number(documentId),
        version: selectedDocument?.version,
        type: actualContentType,
        submissionId: Number(submissionId),
      });
    }

    reset((fieldValues: any) => ({
      ...fieldValues,
      [FormFields.documentId]: documentId,
      [FormFields.documentTitle]: documentTitle,
      [FormFields.content]: documentHtmlContent ?? '',
      [FormFields.webLink]: removeHttpPrefixFromLink(customerLink) || '',
      [FormFields.hasDigitalVersion]: hasDigitalVersion ?? false,
      [FormFields.isCustomerApprovalRequired]: isCustomerApprovalRequired,
    }));
    setIsModalOpen(mode ?? 'Update');
  };

  const allErrors = fileErrors.concat(secondaryDocument.fileErrors);

  const allIsLoading = isLoading || secondaryDocument.isLoading;

  return {
    handleFileSubmit,
    handleUploadClick,
    handleCancel,
    handleRemove,
    handleOpenModal,
    clearErrors,
    fileErrors: allErrors,
    isModalOpen,
    isLoading: allIsLoading,
    uploadProgress,
    secondaryDocument,
  };
};
